import { useState, useEffect, useRef } from "react";

import { useDidMountEffect } from "../../utils/utils";
import { adminEndpoints } from "../../api/endpoints";

export default function useLocations(apiClient, isFocused = true) {
  const [locations, setLocations] = useState(null);

  // pagination
  const [batch, setBatch] = useState(0);
  const [nPages, setNPages] = useState(null);
  const batchSize = 20;
  const lastBatch = useRef(null);

  // search filter
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    if (isFocused) initData();
    else clearData();
  }, [isFocused]);

  useEffect(() => {
    if (!locations) return; // run if 'searchInput' changes, but not on initial render
    const delayDebounceFn = setTimeout(() => {
      if (batch !== 0) setBatch(0);
      else getLocations();
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput]);

  useDidMountEffect(() => {
    // run if 'batch' changes, but not on initial render
    getLocations();
  }, [batch]);

  const locationsRequest = async (_batch) => {
    const params = {
      batch_size: batchSize,
      batch: _batch,
      search: searchInput || null,
      ascending_ids: false,
    };
    const response = await apiClient.get(adminEndpoints.location, params);
    return response;
  };

  const getLocations = async () => {
    try {
      lastBatch.current = batch;
      const response = await locationsRequest(batch);
      setNPages(Number(response.headers.length));
      setLocations(response.data);
      return true;
    } catch (error) {
      console.warn('Error in "getLocations"', error, error.stack);
      return false;
    }
  };

  const addLocations = async () => {
    if (lastBatch.current === null) return 
    if (nPages !== null && lastBatch.current + 1 >= nPages) return true;
    try {
      lastBatch.current = lastBatch.current + 1;
      const response = await locationsRequest(lastBatch.current || 0);
      setLocations((ls) => [...ls, ...response.data]);
      return true;
    } catch (error) {
      console.warn('Error in "addLocations"', error, error.stack);
      return false;
    }
  };

  const loadData = getLocations;
  const clearData = () => setLocations(null);
  const initData = () => {
    if (batch === 0) loadData();
    else setBatch(0);
  };
  const extendData = addLocations;

  return {
    locations,
    pagination: { nPages, page: batch, setPage: setBatch },
    search: { input: searchInput, setInput: setSearchInput },
    data: {
      init: initData,
      update: loadData,
      clear: clearData,
      extend: extendData,
    },
  };
}
